import {defineComponent, ref, computed, watch, onMounted, inject,} from "vue";
import {useUserStore} from "@/app/shared/state/UserDataModule";
import {
    IUserData,
    IInvoiceListItem,
    InvoicesAndPaymentReceiptsQuery,
    IInvoicesAndPaymentReceiptsQuery, OrderDirection
} from "@/api/klip-api.proxy";
import {Mri} from "@/app/mri/shared/services/services";
import DateUtil from '@/app/shared/helpers/date-util';
import {KlAbortController} from "@/app/shared/helpers/kl-abort-controller";
import {useKlipApiProxy} from "@/plugins/proxy-client";

export default defineComponent({
    compatConfig: {
        MODE: 3,
    },
    name: 'KlInvoices',
    props: {
        organisationId: {
            type: String,
            default: null,
            required: false
        },
        userId: {
            type: String,
            default: null,
            required: false
        },
        showOrganisationName: {
            type: Boolean,
            required: false,
            default: false
        }
    },
    setup(props) {
        const klipApiProxy = useKlipApiProxy();
        const fetching = ref<boolean>(true);
        const invoices = ref<IInvoiceListItem[]>(null);
        const _mri_GetInvoicesAndPaymentReceipts_AbortController = new KlAbortController();

        const meta = ref<{
            totalRows: number;
            resultsPerPage: number;
        }>({totalRows: 0, resultsPerPage: 50});

        const today = DateUtil.now();
        const downloadFileUriLoading = ref<boolean>(false);
        const downloadFileUri = ref<string>('');
        const unAvailable = ref<boolean>(false);
        const searchFields = ref<Mri.IInvoicesSearchFields>({
            date: {
                from: today.add(-1, "year").toDate(),
                to: today.toDate()
            }
        });



        const $refs = ref<{
            searchInvoices: any;
        }>(undefined);

        const displayedInvoices = computed((): IInvoiceListItem[] => {
            return invoices.value || [];
        });

        const user = computed((): IUserData => {
            return useUserStore().user;
        });

        const showDownloadLink = computed((): boolean => {
            return !!invoices.value && invoices.value.length > 0;
        });

        const showFinancialServiceWarning = computed((): boolean => {
            return !!props.organisationId && !!props.userId;
        });

        const search = (value: Mri.IInvoicesSearchFields) => {
            if (!value.date.from || !value.date.to) {
                return;
            }

            if (value.date.from.toLocaleDateString() !== searchFields.value.date.from.toLocaleDateString()
                || value.date.to.toLocaleDateString() !== searchFields.value.date.to.toLocaleDateString()
                || value.documentReference != searchFields.value.documentReference) {
                searchFields.value = value;
                fetching.value = true;
                fetchInvoices();
            }
        };

        const onDisplayedInvoicesChanged = (invoices: IInvoiceListItem[]) => {
            meta.value.totalRows = invoices != null ? invoices.length : 0;
        };

        const clearSearch = () => {
            $refs.value.searchInvoices.reset();
        };

        const download = () => {
            downloadFileUriLoading.value = true;
            const link = document.createElement("a");
            link.href = downloadFileUri.value;
            link.click();
            URL.revokeObjectURL(link.href);
            downloadFileUriLoading.value = false;
        };

        const cellParser = (row: any, column: IColumn) => {
            switch (column.key) {
                case "submitTimestamp":
                    return {
                        template: `<div>${DateUtil.formatDate(row[column.key])}</div>`,
                    };
                case "totalPrice":
                    return {
                        template: `<div class="vl-u-align-right">€${row[column.key]}</div>`,
                    };
                case "invoicePdfUrl":
                    return {
                        template: `${
                            row[column.key]
                                ? `<vl-link href="${
                                    row[column.key]
                                }" mod-icon-only icon="file-office-pdf" hidden-text="Download als PDF" class="vl-u-align-center"></vl-link>`
                                : "<span></span>"
                        }`,
                    };
                case "attachmentCsvUrl":
                    return {
                        template: `${
                            row[column.key]
                                ? `<vl-link href="${
                                    row[column.key]
                                }" mod-icon-only icon="file-office-xls" hidden-text="Download als CSV" class="vl-u-align-center"></vl-link>`
                                : "<span></span>"
                        }`,
                    };
                default:
                    if (!row[column.key]) {
                        return {template: `<span></span>`};
                    }
                    return {template: `<div>${row[column.key]}</div>`};
            }
        };

        const columns = ref<IColumn[]>([
            {
                key: 'submitTimestamp',
                label: 'Datum',
                sortable: false,
                isVisible: true,
                width: 7,
                widthUnit: 'em',
                parser: cellParser,
            },
            {
                key: 'documentReference',
                label: 'Doc nr',
                sortable: false,
                isVisible: true,
                width: 11,
                widthUnit: 'em',
                parser: cellParser,
            },
            {
                key: 'organisationName',
                label: 'Ontvanger',
                sortable: false,
                isVisible: props.showOrganisationName,
                width: 15,
                widthUnit: 'em',
                parser: cellParser,
            },
            {
                key: 'customerReference',
                label: 'Eigen referentie',
                sortable: false,
                isVisible: true,
                width: 15,
                widthUnit: 'em',
                parser: cellParser,
            },
            {
                key: 'paymentType',
                label: 'Inhoud',
                sortable: false,
                isVisible: true,
                width: 15,
                widthUnit: 'em',
                parser: cellParser,
            },
            {
                key: 'totalPriceFormatted',
                label: 'Bedrag',
                sortable: false,
                isVisible: true,
                width: 7,
                widthUnit: 'em',
                parser: cellParser,
            },
            {
                key: 'invoicePdfUrl',
                label: 'PDF',
                sortable: false,
                isVisible: true,
                width: 3,
                widthUnit: 'em',
                parser: cellParser,
            },
            {
                key: 'attachmentCsvUrl',
                label: 'CSV',
                sortable: false,
                isVisible: true,
                width: 3,
                widthUnit: 'em',
                parser: cellParser,
            }
        ]);

        const fetchInvoices = () => {
            fetching.value = true;
            const input: IInvoicesAndPaymentReceiptsQuery = {
                fromDate: DateUtil.formatForApi(DateUtil.parseToDayJs(searchFields.value.date.from).startOf("day"), 'YYYY-MM-DD'),
                toDate: DateUtil.formatForApi(DateUtil.parseToDayJs(searchFields.value.date.to).startOf("day"), 'YYYY-MM-DD'),
                docNr: searchFields.value.documentReference,
                klipUserId: props.userId,
                organisationId: props.organisationId,
            };

            klipApiProxy
                .mri_GetInvoicesAndPaymentReceipts(InvoicesAndPaymentReceiptsQuery.fromJS(input), _mri_GetInvoicesAndPaymentReceipts_AbortController.abortAndCreateNewSignal())
                .then((response) => {
                    unAvailable.value = response.result.unavailable || response.isSuccessStatusCode === false;
                    downloadFileUri.value = response.result.downloadUrl;
                    invoices.value = response.result.invoices.map((obj) => ({
                        ...obj,
                        attachmentCsvUrl:
                            obj.isTopUpCredit === false ? obj.attachmentCsvUrl : "",
                        visible: true,
                    }));
                })
                .finally(() => {
                    fetching.value = false;
                });
        };

        watch(displayedInvoices, onDisplayedInvoicesChanged, {
            immediate: false,
            deep: true,
        });

        onMounted(() => {
            fetchInvoices();
        });

        const showUnavailable = computed((): boolean => {
            return unAvailable.value;
        });

        return {
            fetching,
            invoices,
            meta,
            columns,
            downloadFileUriLoading,
            downloadFileUri,
            searchFields,
            displayedInvoices,
            user,
            showDownloadLink,
            showUnavailable,
            showFinancialServiceWarning,
            search,
            onDisplayedInvoicesChanged,
            clearSearch,
            download,
            cellParser,
            fetchInvoices,
        };
    },
});
